import React from 'react';

/**
 * Compact coach panel for bootcamp / first-time nonogram guidance.
 */
export default function TutorialCoach({
    step,
    total,
    title,
    body,
    hint,
    primaryLabel = 'Next',
    secondaryLabel,
    tertiaryLabel,
    onPrimary,
    onSecondary,
    onTertiary,
    highlight,
}) {
    return (
        <div
            className={`atelier-coach${highlight ? ` is-hl-${highlight}` : ''}`}
            role="region"
            aria-label="Tutorial coach"
        >
            <div className="atelier-coach-meta">
                <span className="atelier-coach-step">
                    Tip {step}/{total}
                </span>
                {hint ? <span className="atelier-coach-hint">{hint}</span> : null}
            </div>
            <h3 className="atelier-coach-title">{title}</h3>
            <p className="atelier-coach-body">{body}</p>
            <div className="atelier-coach-actions">
                {secondaryLabel && onSecondary ? (
                    <button type="button" className="atelier-chip-btn" onClick={onSecondary}>
                        {secondaryLabel}
                    </button>
                ) : null}
                {tertiaryLabel && onTertiary ? (
                    <button type="button" className="atelier-chip-btn" onClick={onTertiary}>
                        {tertiaryLabel}
                    </button>
                ) : null}
                <button type="button" className="atelier-btn-primary" onClick={onPrimary}>
                    {primaryLabel}
                </button>
            </div>
        </div>
    );
}

const CELL = { EMPTY: 0, FILLED: 1, MARKED: 2 };

/** Guided steps for the first (and only) super-guided practice board. */
export const GUIDED_BOARD_STEPS = [
    {
        id: 'clues',
        highlight: 'clues',
        paintMode: null,
        title: 'Numbers = runs of clay',
        body: 'This tiny board’s row clues are 2, 4, 4, 2 — and the columns match. A “4” means four orange cells in a row. A “2” means two orange cells (with empties beside them).',
        primaryLabel: 'Next — DRAW the clay',
    },
    {
        id: 'draw',
        highlight: 'draw',
        paintMode: 'draw',
        title: 'DRAW the glowing cells',
        body: 'Orange glow = clay. Tap DRAW (already selected), then paint every glowing cell. Leave the dark corners alone for now.',
        primaryLabel: 'I’ve painted the clay',
        tertiaryLabel: 'Paint clay for me',
    },
    {
        id: 'mark',
        highlight: 'mark',
        paintMode: 'mark',
        title: 'MARK the empty corners',
        body: 'Pale glow = empty. Tap MARK, then put an X on every glowing cell. In nonograms you mark empties so you don’t fill them by mistake.',
        primaryLabel: 'I’ve marked the empties',
        tertiaryLabel: 'Mark empties for me',
    },
    {
        id: 'finish',
        highlight: 'check',
        paintMode: null,
        title: 'Nice — board complete',
        body: 'Every cell is either DRAW or MARK. We check automatically. Next heart slices are yours alone — same rules, no glowing hints.',
        primaryLabel: 'Finish lesson',
    },
];

/** Highlight cells the player should paint for the current guided step. */
export function guidedHighlightsForStep(solution, stepId, grid = null) {
    if (!solution?.length || !stepId) return [];
    const n = solution.length;
    const highlights = [];
    for (let r = 0; r < n; r++) {
        for (let c = 0; c < n; c++) {
            const wantFill = solution[r][c] === 1;
            const cur = grid?.[r]?.[c];
            if (stepId === 'draw' && wantFill) {
                if (cur === CELL.FILLED) continue; // already done
                highlights.push({ r, c, kind: 'tip-fill' });
            }
            if (stepId === 'mark' && !wantFill) {
                if (cur === CELL.MARKED) continue;
                highlights.push({ r, c, kind: 'tip-mark' });
            }
        }
    }
    return highlights;
}

/** True when the guided step’s target cells are all painted correctly. */
export function guidedStepComplete(solution, stepId, grid) {
    if (!solution?.length || !grid?.length) return false;
    if (stepId === 'clues') return true;
    if (stepId === 'finish') {
        return !grid.some((row) => row.some((cell) => cell === CELL.EMPTY));
    }
    const n = solution.length;
    for (let r = 0; r < n; r++) {
        for (let c = 0; c < n; c++) {
            const wantFill = solution[r][c] === 1;
            if (stepId === 'draw' && wantFill && grid[r][c] !== CELL.FILLED) return false;
            if (stepId === 'mark' && !wantFill && grid[r][c] !== CELL.MARKED) return false;
        }
    }
    return true;
}

/** Apply all cells for the current guided paint step. */
export function paintGuidedStep(grid, solution, stepId) {
    if (!grid?.length || !solution?.length) return grid;
    const next = grid.map((row) => [...row]);
    const n = solution.length;
    for (let r = 0; r < n; r++) {
        for (let c = 0; c < n; c++) {
            const wantFill = solution[r][c] === 1;
            if (stepId === 'draw' && wantFill) next[r][c] = CELL.FILLED;
            if (stepId === 'mark' && !wantFill) next[r][c] = CELL.MARKED;
        }
    }
    return next;
}
